import { useGtag } from "#imports";
import type { CartLine, Product } from "~~/shared/types/store";
import { seriesHash } from "~~/shared/constants/allegrohash";
import { useAuthStore } from "~~/stores/authStore";

export interface Ga4Item {
  item_id: string;
  item_name: string;
  affiliation?: string;
  coupon?: string;
  currency?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  // group_id serii rozmiarowej = allegrohash (spójne z g:item_group_id w feedzie GMC).
  // Segmentify grupuje po nim warianty tego samego produktu na warstwie danych.
  item_group_id?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
}

export interface ItemListContext {
  itemListId?: string;
  itemListName?: string;
  index?: number;
}

export interface PromotionContext {
  promotionId?: string;
  promotionName?: string;
  creativeName?: string;
  creativeSlot?: string;
  locationId?: string;
}

const CURRENCY = "PLN";

/** Skąd padł prompt instalacji PWA — do analizy konwersji per powierzchnia. */
export type PwaPromptSource = "auto" | "manual" | "post-purchase";
/** Platforma instalacji: android = beforeinstallprompt, ios = ręczne instrukcje. */
export type PwaPlatform = "android" | "ios" | "other";

function safeNumber(value: unknown): number | undefined {
  if (typeof value !== "number") return undefined;
  return Number.isFinite(value) ? value : undefined;
}

function discountFrom(price: number, compareAt?: number): number | undefined {
  if (!compareAt || compareAt <= price) return undefined;
  const diff = compareAt - price;
  return Math.round(diff * 100) / 100;
}

function sumItemsValue(items: Ga4Item[]): number {
  const sum = items.reduce((acc, item) => {
    const price = item.price ?? 0;
    const quantity = item.quantity ?? 1;
    return acc + price * quantity;
  }, 0);
  return Math.round(sum * 100) / 100;
}

/**
 * Wypycha zdarzenie e-commerce w formacie NATYWNYM dla kontenera GTM:
 * zagnieżdżony obiekt `ecommerce` na warstwie danych (NIE komenda gtag.js).
 *
 * Kontener GTM-T4QW2CD czyta produkty/ceny/ilości ze zmiennych warstwy danych
 * `ecommerce.items`, `ecommerce.value` itd. Komenda `gtag('event', …, {flat})`
 * tych zmiennych NIE wypełnia (parametry leżą płasko, nie pod `ecommerce`),
 * dlatego GA4/Ads/Meta widziały zdarzenia bez produktów. Push poniżej naprawia
 * to dla całej ścieżki zakupowej.
 *
 * `ecommerce: null` przed właściwym pushem czyści poprzedni obiekt, by pozycje
 * z wcześniejszego zdarzenia nie wyciekały do następnego (zalecenie GA4).
 */
function pushEcommerce(event: string, ecommerce: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const w = window as Window & { dataLayer?: Record<string, unknown>[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ ecommerce: null });
  w.dataLayer.push({ event, ecommerce });
}

/**
 * Wypycha dowolny obiekt na warstwę danych (page_view, user_data itp.).
 * W przeciwieństwie do pushEcommerce NIE zeruje `ecommerce` — te zdarzenia nie
 * niosą bloku produktowego GA4. Odczytywane m.in. przez Segmentify (integracja
 * dataLayer) i tagi GTM.
 */
function pushDataLayer(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const w = window as Window & { dataLayer?: Record<string, unknown>[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}

// Ostatnio wypchnięta tożsamość — dedup na poziomie modułu (wiele instancji
// useAnalytics w komponentach), by nie dublować user_data przy każdej hydracji.
let lastIdentitySig = "";
// Ostatni obiekt user_data — powtarzany przy każdej nawigacji SPA
// (republishIdentity), bo partner konsumujący warstwę danych startuje później
// niż nasz pierwszy push i nie zawsze widzi jego historię.
let lastIdentityPayload: Record<string, unknown> | null = null;

/** Sygnatura gościa — jedna dla wszystkich niezalogowanych sesji (dedup). */
const GUEST_IDENTITY_SIG = "guest";

/** Tożsamość klienta w kształcie, w jakim trzyma ją authStore / GET account/info. */
export interface IdentityUser {
  id: number | string;
  email?: string;
  firstName?: string;
  lastName?: string;
  newsletter?: boolean;
  /** RRRR-MM-DD; brak daty (PrestaShop '0000-00-00') = pole nie leci. */
  birthday?: string;
  /** id_gender 1/2 zmapowane w mapAccountIdentity; 0 („nie podano") = pole nie leci. */
  gender?: "male" | "female";
}

/**
 * Obiekt `user` warstwy danych — jedno miejsce mapowania camelCase → snake_case.
 * `birthday`/`gender` to atrybuty personalizacji dla partnera (Segmentify);
 * pomijamy je, gdy klient ich nie podał, zamiast wysyłać puste stringi.
 */
function toIdentityPayload(user: IdentityUser) {
  return {
    id: String(user.id),
    email: user.email || undefined,
    first_name: user.firstName || undefined,
    last_name: user.lastName || undefined,
    newsletter: Boolean(user.newsletter),
    birthday: user.birthday || undefined,
    gender: user.gender || undefined,
  };
}

/**
 * Sygnatura dedupu z KAŻDEGO wypychanego pola.
 *
 * Wcześniej brała tylko `id|email|newsletter`, więc zapis „Moje dane" zmieniający
 * samo imię/nazwisko nie zmieniał sygnatury i push `user_data` był zjadany — po
 * aktualizacji profilu partner (Segmentify) dalej widział STARY obiekt.
 */
function identitySig(payload: ReturnType<typeof toIdentityPayload>) {
  return [
    payload.id,
    payload.email ?? "",
    payload.first_name ?? "",
    payload.last_name ?? "",
    payload.newsletter ? 1 : 0,
    payload.birthday ?? "",
    payload.gender ?? "",
  ].join("|");
}

export function toGa4ItemFromProduct(
  product: Product,
  context: ItemListContext & { quantity?: number; variant?: string } = {},
): Ga4Item {
  const price = safeNumber(product.price) ?? 0;
  return {
    // item_id ujednolicone na NUMERYCZNE id produktu (PrestaShop id_product) we
    // WSZYSTKICH zdarzeniach GA4 — spójne z feedem GMC (g:id), przyciskiem
    // add-to-cart (POST /rest/cart -> productId) oraz Meta/edrone. Wcześniej
    // zdarzenia produktowe (view_item / add_to_cart / add_to_wishlist) brały
    // `sku` (= PrestaShop reference / MPN), a koszykowe (view_cart / remove /
    // purchase) numeryczne id -> Segmentify widział dwa różne identyfikatory
    // tego samego produktu. Zob. toGa4ItemFromCartLine (line.productId).
    item_id: product.id,
    item_name: product.name,
    item_brand: product.brand || undefined,
    item_category: product.categoryName || undefined,
    // group_id = allegrohash (seria rozmiarowa) — pomijamy pusty ORAZ wartownik
    // 'not known yet', który backend wystawia surowo (zob. seriesHash).
    item_group_id: seriesHash(product.allegrohash),
    item_variant: context.variant ?? product.defaultSize ?? undefined,
    price,
    discount: discountFrom(price, safeNumber(product.compareAtPrice)),
    quantity: context.quantity,
    index: context.index,
    item_list_id: context.itemListId,
    item_list_name: context.itemListName,
    currency: CURRENCY,
  };
}

export function toGa4ItemFromCartLine(
  line: CartLine,
  context: ItemListContext = {},
): Ga4Item {
  const price = safeNumber(line.price) ?? 0;
  return {
    item_id: line.productId,
    item_name: line.name,
    item_brand: line.brand || undefined,
    // group_id = allegrohash (seria rozmiarowa) — spójne ze zdarzeniami produktowymi i feedem.
    item_group_id: seriesHash(line.allegrohash),
    item_variant:
      [line.size, line.color].filter(Boolean).join(" / ") || undefined,
    price,
    discount: discountFrom(price, safeNumber(line.compareAtPrice)),
    quantity: line.quantity,
    index: context.index,
    item_list_id: context.itemListId,
    item_list_name: context.itemListName,
    currency: CURRENCY,
  };
}

export function toGa4ItemList(
  products: Product[],
  context: Omit<ItemListContext, "index"> = {},
): Ga4Item[] {
  return products.map((product, index) =>
    toGa4ItemFromProduct(product, { ...context, index }),
  );
}

export function useAnalytics() {
  const { gtag } = useGtag();
  const meta = useMetaTracking();
  const edrone = useEdroneTracking();
  const auth = useAuthStore();

  function trackViewItem(product: Product, context: ItemListContext = {}) {
    const item = toGa4ItemFromProduct(product, { ...context, quantity: 1 });
    pushEcommerce("view_item", {
      currency: CURRENCY,
      value: item.price ?? 0,
      items: [item],
    });
    meta.fire(
      "ViewContent",
      { content_ids: [String(product.id)], content_type: "product", value: item.price ?? 0, currency: CURRENCY },
      { id_product: Number(product.id) },
    );
    edrone.trackProductView(product.id);
  }

  function trackViewItemList(
    products: Product[],
    context: { itemListId?: string; itemListName?: string },
  ) {
    if (!products.length) return;
    const items = toGa4ItemList(products, context);
    pushEcommerce("view_item_list", {
      item_list_id: context.itemListId,
      item_list_name: context.itemListName,
      items,
    });
  }

  function trackSelectItem(
    product: Product,
    context: ItemListContext = {},
  ) {
    const item = toGa4ItemFromProduct(product, context);
    pushEcommerce("select_item", {
      item_list_id: context.itemListId,
      item_list_name: context.itemListName,
      items: [item],
    });
  }

  function trackAddToCart(
    product: Product,
    quantity = 1,
    context: { variant?: string } & ItemListContext = {},
  ) {
    const item = toGa4ItemFromProduct(product, { ...context, quantity });
    pushEcommerce("add_to_cart", {
      currency: CURRENCY,
      value: Math.round((item.price ?? 0) * quantity * 100) / 100,
      items: [item],
    });
    const atcCartId = useCartId();
    meta.fire(
      "AddToCart",
      { content_ids: [String(product.id)], content_type: "product", value: Math.round((item.price ?? 0) * quantity * 100) / 100, currency: CURRENCY, contents: [{ id: String(product.id), quantity }] },
      { id_product: Number(product.id), quantity, ...(atcCartId ? { id_cart: atcCartId } : {}) },
    );
    edrone.trackAddToCart(product.id);
  }

  function trackRemoveFromCart(line: CartLine, quantity?: number) {
    const qty = quantity ?? line.quantity;
    const item = { ...toGa4ItemFromCartLine(line), quantity: qty };
    pushEcommerce("remove_from_cart", {
      currency: CURRENCY,
      value: Math.round((item.price ?? 0) * qty * 100) / 100,
      items: [item],
    });
  }

  function trackCartQuantityDelta(line: CartLine, delta: number) {
    if (!delta) return;
    if (delta > 0) {
      const item = { ...toGa4ItemFromCartLine(line), quantity: delta };
      const value = Math.round((item.price ?? 0) * delta * 100) / 100;
      pushEcommerce("add_to_cart", {
        currency: CURRENCY,
        value,
        items: [item],
      });
      // Parytet z trackAddToCart: zwiększenie ilości (CartDrawer / strona koszyka) to też
      // AddToCart — Meta pixel + most CAPI, żeby każde realne dodanie było liczone.
      const atcCartId = useCartId();
      meta.fire(
        "AddToCart",
        { content_ids: [String(line.productId)], content_type: "product", value, currency: CURRENCY, contents: [{ id: String(line.productId), quantity: delta }] },
        { id_product: Number(line.productId), quantity: delta, ...(atcCartId ? { id_cart: atcCartId } : {}) },
      );
    } else {
      trackRemoveFromCart(line, Math.abs(delta));
    }
  }

  function trackViewCart(lines: CartLine[], total?: number) {
    if (!lines.length) return;
    const items = lines.map((line) => toGa4ItemFromCartLine(line));
    pushEcommerce("view_cart", {
      currency: CURRENCY,
      value: safeNumber(total) ?? sumItemsValue(items),
      items,
    });
    const vcCartId = useCartId();
    meta.fire("ViewCart",
      { content_type: "product", value: safeNumber(total) ?? sumItemsValue(items), currency: CURRENCY },
      vcCartId ? { id_cart: vcCartId } : {},
    );
  }

  function trackBeginCheckout(
    lines: CartLine[],
    options: { value?: number; coupon?: string } = {},
  ) {
    if (!lines.length) return;
    const items = lines.map((line) => toGa4ItemFromCartLine(line));
    pushEcommerce("begin_checkout", {
      currency: CURRENCY,
      value: safeNumber(options.value) ?? sumItemsValue(items),
      coupon: options.coupon || undefined,
      items,
    });
    const bcCartId = useCartId();
    meta.fire("InitiateCheckout",
      { content_type: "product", value: safeNumber(options.value) ?? sumItemsValue(items), currency: CURRENCY },
      bcCartId ? { id_cart: bcCartId } : {},
    );
  }

  function trackAddShippingInfo(
    lines: CartLine[],
    options: { value?: number; coupon?: string; shippingTier?: string } = {},
  ) {
    if (!lines.length) return;
    const items = lines.map((line) => toGa4ItemFromCartLine(line));
    pushEcommerce("add_shipping_info", {
      currency: CURRENCY,
      value: safeNumber(options.value) ?? sumItemsValue(items),
      coupon: options.coupon || undefined,
      shipping_tier: options.shippingTier || undefined,
      items,
    });
    const asiCartId = useCartId();
    meta.fire("AddShippingInfo",
      { content_type: "product", value: safeNumber(options.value) ?? sumItemsValue(items), currency: CURRENCY, ...(options.shippingTier ? { shipping_tier: options.shippingTier } : {}) },
      asiCartId ? { id_cart: asiCartId } : {},
    );
  }

  function trackAddPaymentInfo(
    lines: CartLine[],
    options: { value?: number; coupon?: string; paymentType?: string } = {},
  ) {
    if (!lines.length) return;
    const items = lines.map((line) => toGa4ItemFromCartLine(line));
    pushEcommerce("add_payment_info", {
      currency: CURRENCY,
      value: safeNumber(options.value) ?? sumItemsValue(items),
      coupon: options.coupon || undefined,
      payment_type: options.paymentType || undefined,
      items,
    });
    const apiCartId = useCartId();
    meta.fire("AddPaymentInfo",
      { content_type: "product", value: safeNumber(options.value) ?? sumItemsValue(items), currency: CURRENCY, ...(options.paymentType ? { payment_type: options.paymentType } : {}) },
      { value: safeNumber(options.value) ?? sumItemsValue(items), ...(apiCartId ? { id_cart: apiCartId } : {}) },
    );
  }

  function trackPurchase(payload: {
    transactionId: string | number;
    lines: CartLine[];
    value: number;
    tax?: number;
    shipping?: number;
    coupon?: string;
    affiliation?: string;
    /**
     * Pomiń przeglądarkowy Meta Pixel Purchase. Używane, gdy transactionId NIE
     * jest id zamówienia (np. Autopay — polling nie zwraca order_id): backendowy
     * CAPI wysyła Purchase z eventId `purchase_<id_order>`, więc pixel z innym
     * eventId nie zdeduplikowałby się i Meta policzyłaby zakup podwójnie.
     * GA4 (dataLayer) leci zawsze — nie ma server-side odpowiednika.
     */
    skipMeta?: boolean;
  }) {
    const items = payload.lines.map((line) => toGa4ItemFromCartLine(line));
    pushEcommerce("purchase", {
      transaction_id: String(payload.transactionId),
      currency: CURRENCY,
      value: Math.round(payload.value * 100) / 100,
      tax: safeNumber(payload.tax),
      shipping: safeNumber(payload.shipping),
      coupon: payload.coupon || undefined,
      affiliation: payload.affiliation || undefined,
      items,
    });
    // edrone `order` — kluczowy dla atrybucji: łączy anonimową sesję (cookie
    // fp_sid ustawiane przez SDK) z zakupem. Leci ZAWSZE, także gdy skipMeta.
    const buyer = auth.currentUser;
    edrone.trackOrder({
      order_id: String(payload.transactionId),
      product_ids: payload.lines.map((l) => String(l.productId)).join(","),
      product_counts: payload.lines.map((l) => String(l.quantity ?? 1)).join(","),
      order_currency: CURRENCY,
      base_currency: CURRENCY,
      order_payment_value: String(Math.round(payload.value * 100) / 100),
      base_payment_value: String(Math.round(payload.value * 100) / 100),
      event_utc_date: new Date().toISOString(),
      ...(buyer?.email ? { email: buyer.email } : {}),
      ...(buyer?.firstName ? { first_name: buyer.firstName } : {}),
      ...(buyer?.lastName ? { last_name: buyer.lastName } : {}),
      ...(buyer?.id ? { user_id: String(buyer.id) } : {}),
    });
    if (payload.skipMeta) return;
    // Guard: Meta wymaga custom_data.value > 0 dla Purchase — event bez dodatniej
    // wartości jest oznaczany jako niekompletny („Purchase bez wartości zamówienia").
    // Gdy wartość jest niepoprawna (0 / NaN / ujemna) POMIJAMY przeglądarkowy Pixel:
    // serwerowy CAPI i tak wysyła Purchase z tym samym eventId `purchase_<id_order>`
    // i poprawną wartością z zamówienia (dedup po eventId), więc konwersja nie ginie,
    // a Meta nie dostaje eventu bez wartości.
    const metaValue = Math.round(payload.value * 100) / 100;
    if (!Number.isFinite(metaValue) || metaValue <= 0) {
      if (import.meta.dev) {
        console.warn(
          "[analytics] Pominięto Meta Pixel Purchase — brak dodatniej wartości",
          { transactionId: payload.transactionId, value: payload.value },
        );
      }
      return;
    }
    meta.fire(
      "Purchase",
      {
        content_ids: payload.lines.map((l) => String(l.productId)),
        content_type: "product",
        value: metaValue,
        currency: CURRENCY,
        num_items: payload.lines.reduce((a, l) => a + (l.quantity ?? 1), 0),
      },
      {},
      { eventId: `purchase_${payload.transactionId}`, useBridge: false },
    );
  }

  function trackAddToWishlist(product: Product) {
    const item = toGa4ItemFromProduct(product, { quantity: 1 });
    pushEcommerce("add_to_wishlist", {
      currency: CURRENCY,
      value: item.price ?? 0,
      items: [item],
    });
    meta.fire(
      "AddToWishlist",
      { content_ids: [String(product.id)], content_type: "product", value: item.price ?? 0, currency: CURRENCY },
      { id_product: Number(product.id) },
    );
  }

  /**
   * Usunięcie z ulubionych — symetria do add_to_wishlist na warstwie danych
   * (Segmentify / GA4). Meta nie ma standardowego zdarzenia RemoveFromWishlist,
   * więc emitujemy tylko dataLayer.
   */
  function trackRemoveFromWishlist(product: Product) {
    const item = toGa4ItemFromProduct(product, { quantity: 1 });
    pushEcommerce("remove_from_wishlist", {
      currency: CURRENCY,
      value: item.price ?? 0,
      items: [item],
    });
  }

  function trackSearch(searchTerm: string) {
    const term = searchTerm.trim();
    if (!term) return;
    gtag("event", "search", { search_term: term });
    meta.fire("Search", { search_string: term }, { search_query: term });
  }

  function trackLogin(method: "password" | "google" | "facebook" = "password") {
    gtag("event", "login", { method });
  }

  function trackSignUp(method: "password" | "google" | "facebook" = "password") {
    gtag("event", "sign_up", { method });
  }

  function trackPwaPromptShown(source: PwaPromptSource, platform: PwaPlatform) {
    gtag("event", "pwa_prompt_shown", { source, platform });
  }

  function trackPwaPromptAccepted(source: PwaPromptSource, platform: PwaPlatform) {
    gtag("event", "pwa_prompt_accepted", { source, platform });
  }

  function trackPwaPromptDismissed(source: PwaPromptSource) {
    gtag("event", "pwa_prompt_dismissed", { source });
  }

  function trackPwaIosInstructionsShown(source: PwaPromptSource) {
    gtag("event", "pwa_ios_instructions_shown", { source });
  }

  function trackPwaInstalled() {
    gtag("event", "pwa_installed");
  }

  function trackSelectPromotion(code: string, context: PromotionContext = {}) {
    gtag("event", "select_promotion", {
      promotion_id: context.promotionId || code,
      promotion_name: context.promotionName || code,
      creative_name: context.creativeName || undefined,
      creative_slot: context.creativeSlot || undefined,
      location_id: context.locationId || undefined,
    });
  }

  /** edrone `category_view` + Meta ViewCategory (pixel + CAPI). GA4 pokrywa view_item_list. */
  function trackViewCategory(categoryId: string | number, categoryName?: string) {
    edrone.trackCategoryView(categoryId, categoryName);
    meta.fire(
      "ViewCategory",
      { content_type: "product_group", ...(categoryName ? { content_category: categoryName } : {}) },
      { id_category: Number(categoryId) || 0 },
    );
  }

  /**
   * edrone `subscribe` + Meta Newsletter (pixel + CAPI). Tag edrone mówi, skąd
   * przyszedł zapis: `Footer` = sekcja newslettera w treści strony, `Landing` =
   * landing `/newsletter`, a kampania marki ma własny tag z konfiguracji
   * (`newsletterCampaign.edroneTag`). null = do edrone nic nie idzie (formularz
   * podpina samo edrone), Meta zostaje.
   */
  function trackNewsletterSubscribe(email: string, source: string | null = "Footer") {
    if (source !== null) edrone.trackSubscribe({ email, customer_tags: source });
    // E-mail idzie tylko do mostu CAPI (backend go hashuje); do przeglądarkowego pixela nie.
    meta.fire("Newsletter", {}, { email });
  }

  /** Meta Contact (pixel + CAPI) — po udanym wysłaniu formularza kontaktowego. */
  function trackContact(subject?: string) {
    meta.fire(
      "Contact",
      subject ? { content_name: subject } : {},
      { subject: subject || "" },
    );
  }

  /**
   * GA4 `generate_lead` — wysłane zapytanie o wycenę (sklep katalogowy, proles).
   * Zdarzenie warstwy danych: czyta je tag zdarzenia GA4 w kontenerze GTM marki
   * (wyzwalacz Custom Event `generate_lead`, oznaczony w GA4 jako zdarzenie
   * kluczowe). Bez danych osobowych i bez wartości — wycenę robi dopiero
   * sprzedawca. `quote_list` = zapytanie z Listy Zapytania, `product` = o jeden
   * produkt (karta, kafel, szybki podgląd).
   */
  function trackGenerateLead(payload: { leadSource: "product" | "quote_list"; itemsCount: number }) {
    pushDataLayer({
      event: "generate_lead",
      lead_source: payload.leadSource,
      items_count: payload.itemsCount,
    });
  }

  /** edrone `subscribe` po rejestracji konta (tag Register). */
  function trackRegistration(payload: { email: string; firstName?: string; lastName?: string }) {
    edrone.trackSubscribe({
      email: payload.email,
      customer_tags: "Register",
      ...(payload.firstName ? { first_name: payload.firstName } : {}),
      ...(payload.lastName ? { last_name: payload.lastName } : {}),
    });
  }

  /**
   * Odsłona SPA — jawny page_view na dataLayer (hydracja + każda nawigacja).
   * GA4 zbiera odsłony przez trigger History Change w GTM; ten push dokłada
   * czytelny obiekt `page` dla Segmentify, który po załadowaniu SDK nie wykrywa
   * nawigacji SPA. Zob. plugins/datalayer-pageview.client.ts.
   */
  function trackPageView(page: { path: string; title?: string; type?: string }) {
    pushDataLayer({
      event: "page_view",
      page: {
        path: page.path,
        title:
          page.title ??
          (typeof document !== "undefined" ? document.title : undefined),
        type: page.type || "other",
      },
    });
  }

  /**
   * Wspólny push tożsamości — jeden kształt zdarzenia dla zalogowanego i gościa,
   * z dedupem po sygnaturze. Zapamiętuje payload, by republishIdentity mogło go
   * powtórzyć przy nawigacji SPA.
   */
  function pushIdentity(
    sig: string,
    user: Record<string, unknown> | null,
    loggedIn: boolean,
    options: { force?: boolean } = {},
  ) {
    if (!options.force && sig === lastIdentitySig) return;
    lastIdentitySig = sig;
    lastIdentityPayload = { event: "user_data", user, logged_in: loggedIn };
    pushDataLayer(lastIdentityPayload);
    if (typeof window !== "undefined") {
      (window as Window & { __user?: unknown }).__user = user;
    }
  }

  /**
   * Identyfikacja zalogowanego użytkownika dla warstwy danych (Segmentify / GA4):
   * push `user_data` + window.__user (id, email, imię, nazwisko, newsletter).
   * Newsletter = zgoda e-mail. Dedup po sygnaturze (lastIdentitySig), bo useAuth
   * (a pośrednio i to) bywa wołane w wielu komponentach → unikamy dublowania.
   * Sygnatura obejmuje wszystkie wypychane pola (zob. identitySig), więc realna
   * zmiana profilu — także samego imienia — ogłasza się partnerowi ponownie.
   */
  function identifyUser(user: IdentityUser, options: { force?: boolean } = {}) {
    const payload = toIdentityPayload(user);
    pushIdentity(identitySig(payload), payload, true, options);
  }

  /**
   * Zapis danych konta („Moje dane") — `user_data` z AKTUALNYM obiektem klienta.
   *
   * Wołane po potwierdzonym zapisie profilu / danych logowania. `force`, bo push
   * ma polecieć po KAŻDYM udanym zapisie: zmiana pola, którego obiekt tożsamości
   * nie niesie (telefon), nie rusza sygnatury, a partner (Segmentify) oczekuje
   * zdarzenia jako sygnału „dane konta zaktualizowane".
   * Dublet identycznego obiektu jest bezpieczny — tak samo działa republishIdentity
   * przy każdej nawigacji SPA.
   */
  function trackAccountUpdate(user: IdentityUser) {
    identifyUser(user, { force: true });
  }

  /**
   * Gość / sesja niezalogowana — JAWNY `user_data` z `user: null`.
   *
   * Bez tego partner (Segmentify) nie odróżnia „to gość" od „profil jeszcze się
   * nie dociągnął": tożsamość zna dopiero odpowiedź GET /api/store/account/info,
   * która leci po hydracji i dla gościa kończy się 401. Deterministyczny sygnał
   * zamyka to okno niepewności.
   */
  function identifyGuest() {
    pushIdentity(GUEST_IDENTITY_SIG, null, false);
  }

  /**
   * Wylogowanie — `user_logout`, a zaraz po nim `user_data` gościa (sesja dalej
   * istnieje, tylko bez tożsamości).
   */
  function resetUser() {
    const hadIdentity =
      lastIdentitySig !== "" && lastIdentitySig !== GUEST_IDENTITY_SIG;
    if (hadIdentity) {
      pushDataLayer({ event: "user_logout", user: null });
    }
    identifyGuest();
  }

  /**
   * Jedno wejście dla pluginu tożsamości (plugins/analytics-identity.client.ts):
   * ogłasza zalogowanego albo gościa, ale WYŁĄCZNIE gdy tożsamość jest już
   * rozstrzygnięta (authStore.isResolved). Przed rozstrzygnięciem `user === null`
   * znaczy „profil w drodze" — ogłoszenie wtedy gościa dawało partnerowi najpierw
   * sesję anonimową, a chwilę później zalogowaną (źródło rozjazdu w `user_data`).
   */
  function syncIdentity(state: {
    resolved: boolean;
    user: (IdentityUser & { isGuest?: boolean }) | null;
  }) {
    if (!state.resolved) return;
    if (state.user && !state.user.isGuest) {
      identifyUser(state.user);
      return;
    }
    // resetUser, nie samo identifyGuest — dorzuca `user_logout`, gdy sesja BYŁA
    // zalogowana (ścieżka wylogowania woła authStore.clearUser()).
    resetUser();
  }

  /**
   * Ponawia ostatni `user_data` BEZ zmiany sygnatury — wołane przy każdej
   * nawigacji SPA (plugins/datalayer-pageview.client.ts) tuż przed `page_view`.
   *
   * Po co: SDK partnera ładujemy dopiero przy pierwszej interakcji (CWV), a jego
   * integracja z warstwą danych podmienia `dataLayer.push`, więc widzi wyłącznie
   * pushe PO swoim starcie. Jednorazowy push tożsamości z chwili hydracji bywał
   * dla niego niewidoczny („user_data niestabilny"); powtórka przy każdej odsłonie
   * daje mu ten obiekt niezależnie od momentu startu.
   */
  function republishIdentity() {
    if (!lastIdentityPayload) return;
    pushDataLayer(lastIdentityPayload);
  }

  return {
    trackViewItem,
    trackViewCategory,
    trackNewsletterSubscribe,
    trackContact,
    trackGenerateLead,
    trackRegistration,
    trackViewItemList,
    trackSelectItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackCartQuantityDelta,
    trackViewCart,
    trackBeginCheckout,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
    trackAddToWishlist,
    trackRemoveFromWishlist,
    trackPageView,
    identifyUser,
    identifyGuest,
    trackAccountUpdate,
    syncIdentity,
    republishIdentity,
    resetUser,
    trackSearch,
    trackLogin,
    trackSignUp,
    trackSelectPromotion,
    trackPwaPromptShown,
    trackPwaPromptAccepted,
    trackPwaPromptDismissed,
    trackPwaIosInstructionsShown,
    trackPwaInstalled,
  };
}
